import { createStore } from "vuex";
import { clearAllStoredQueries } from "@/utils/listState";

// is_read arrives as 0, "0", null or undefined depending on the endpoint.
const isUnreadNotification = (n) => n.is_read === 0 || n.is_read === "0" || !n.is_read;

function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

function isTokenExpired(token) {
  if (!token) return true;
  try {
    const decoded = parseJwt(token);
    if (!decoded || !decoded.exp) return false; // If no expiration, consider valid
    
    // Check if token is expired (exp is in seconds, Date.now() is in milliseconds)
    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
  } catch (e) {
    return true; // If parsing fails, consider token invalid
  }
}

export default createStore({
  state: {
    isLoggedIn: JSON.parse(sessionStorage.getItem('isLoggedIn')) || false,
    isLoading: false,
    userToken: sessionStorage.getItem('userToken') || null,
    user: JSON.parse(sessionStorage.getItem('user')) || null,
    sessionExpired: false,
    notifications: [],
    unreadCount: 0,
  },
  getters: {
    isLoggedIn: (state) => {
      // Check if token exists and is not expired
      const token = state.userToken || sessionStorage.getItem('userToken');
      const isValid = !!token && !isTokenExpired(token);
      // Return true if token is valid OR if we are in "needs re-login" state
      // This keeps the header visible during session expiration.
      return isValid || state.sessionExpired;
    },
    getRole: (state) => {
      return state.user;
    },
    authToken: (state) => {
      return state.userToken;
    },
    loggedUser: (state) => {
      if (state.user && state.user.data && state.user.data.user_name) return state.user;
      
      // Fallback 1: reload from sessionStorage if state is cleared but item exists
      try {
        const stored = JSON.parse(sessionStorage.getItem('user'));
        if (stored && stored.data && stored.data.user_name) return stored;
      } catch (e) { /* ignore */ }

      // Fallback 2: Persistent username even if 'user' is wiped (for re-login overlay)
      const lastUsername = localStorage.getItem('last_username');
      if (lastUsername) {
        return { 
          data: { user_name: lastUsername },
          status: true
        };
      }
      
      return state.user || {};
    },
    userRole: (state) => {
      return state.user?.data?.role_name || "";
    },
    // Backend-computed general-privilege flag (see role_permissions /
    // Permission.isPrivileged), returned by /getUserDetails. Used by the
    // router guard for routes flagged meta.requiresPrivilege, and available
    // for components to check instead of hardcoding role_id comparisons.
    isPrivileged: (state) => {
      return !!state.user?.data?.is_privileged;
    },
    isTokenValid: (state) => {
      const token = state.userToken || sessionStorage.getItem('userToken');
      return !!token && !isTokenExpired(token);
    },
    isSessionExpired: (state) => state.sessionExpired,
    // True when user object exists but token is gone (re-login state)
    needsRelogin: (state) => state.sessionExpired && !!state.user,
    allNotifications: (state) => state.notifications,
    unreadNotificationCount: (state) => state.unreadCount,
  },
  mutations: {
    LOGIN: function (state) {
      state.isLoading = true;
    },
    LOGIN_SUCCESS: function (state, response) {
      state.isLoggedIn = true;
      state.isLoading = false;
      state.userToken = response.token;

      // Decode token to get user data
      const decoded = parseJwt(response.token);
      if (decoded) {
        state.user = {
          status: true,
          message: "Login Successfully",
          token: response.token,
          data: decoded
        };
        
        // Persist username specifically for re-login fallback
        // Priority: Passed username > JWT user_name > JWT sub
        const uname = response.user_name || decoded.user_name || decoded.sub;
        if (uname) {
          localStorage.setItem('last_username', uname);
          // Ensure it's in the state user object too
          if (!state.user.data.user_name) state.user.data.user_name = uname;
        }
      } else if (response.user_name) {
        // Even if decoding fails, if we have a username, keep a minimal user state
        state.user = {
          status: true,
          data: { user_name: response.user_name }
        };
        localStorage.setItem('last_username', response.user_name);
      }

      // Clear session expired status on success
      state.sessionExpired = false;

      // Persist the data in sessionStorage
      sessionStorage.setItem('isLoggedIn', JSON.stringify(state.isLoggedIn));
      sessionStorage.setItem('userToken', state.userToken);
      sessionStorage.setItem('user', JSON.stringify(state.user));
    },
    SET_USER_DATA: function (state, userData) {
      if (state.user) {
        state.user.data = {
          ...state.user.data,
          ...userData
        };
        if (userData.user_name) {
          localStorage.setItem('last_username', userData.user_name);
        } else if (state.user.data.user_name) {
          localStorage.setItem('last_username', state.user.data.user_name);
        }
        sessionStorage.setItem('user', JSON.stringify(state.user));
      }
    },
    LOGOUT: function (state) {
      state.isLoggedIn = false;
      state.userToken = null;
      state.user = {};
      state.sessionExpired = false;
      sessionStorage.removeItem('isLoggedIn');
      sessionStorage.removeItem('userToken');
      sessionStorage.removeItem('user');
      clearAllStoredQueries(sessionStorage);
    },
    // Called on API 401: wipe token but keep user so re-login overlay can show saved username
    SESSION_EXPIRED: function (state) {
      state.isLoggedIn = false;
      state.userToken = null;
      state.sessionExpired = true;
      sessionStorage.removeItem('isLoggedIn');
      sessionStorage.removeItem('userToken');
      // Intentionally keep 'user' in sessionStorage for username display
    },
    CLEAR_SESSION_EXPIRED: function (state) {
      state.sessionExpired = false;
    },
    SET_NOTIFICATIONS: function (state, payload) {
      const notifications = payload.notifications || payload || [];
      const unreadCount = payload.unread_count;

      // Helper to parse date and time for sorting
      const parseDateTime = (n) => {
        if (!n?.notification_date) return 0;
          const [d, m, y] = n.notification_date.split("-");
          const datePart = new Date(Number(y), Number(m) - 1, Number(d));
        
        if (n?.notificationTime) {
          const [time, modifier] = n.notificationTime.split(" ");
          let [hours, minutes] = time.split(":");
          if (hours === "12") hours = "00";
          if (modifier === "PM") hours = parseInt(hours, 10) + 12;
          datePart.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        }
        return datePart.getTime();
      };

      // Sort notifications descending (newest first)
      state.notifications = notifications.sort((a, b) => parseDateTime(b) - parseDateTime(a));
      
      if (unreadCount !== undefined) {
        state.unreadCount = unreadCount;
      } else {
        state.unreadCount = notifications.filter(isUnreadNotification).length;
      }
    },
    MARK_NOTIFICATIONS_READ: function (state, ids) {
      const idSet = new Set(ids);
      state.notifications = (state.notifications || []).map(n =>
        idSet.has(n.notification_id) ? { ...n, is_read: 1 } : n
      );
      state.unreadCount = state.notifications.filter(isUnreadNotification).length;
    },
  },
  actions: {
    login: function ({ commit }, user) {
      commit("LOGIN_SUCCESS", user);
    },
    fetchUserDetails: async function ({ commit }) {
      try {
        const apis = (await import("../apis/index")).default;
        const response = await apis.getUserDetails();
        if (response.data.status === true) {
          commit("SET_USER_DATA", response.data);
          return response.data;
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    },
    logout: function ({ commit }) {
      commit("LOGOUT");
    },
    expireSession: function ({ commit }) {
      commit("SESSION_EXPIRED");
    },
    clearSessionExpired: function ({ commit }) {
      commit("CLEAR_SESSION_EXPIRED");
    },
    fetchNotifications: async function ({ commit, state }) {
      try {
        const apis = (await import("../apis/index")).default;
        const employee_id = state.user?.data?.employee_id;
        if (!employee_id) return;
        
        const response = await apis.checkUnReadNotification({ employee_id });
        if (response.data.status === true) {
          commit("SET_NOTIFICATIONS", response.data.data || { notifications: [], unread_count: 0 });
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    },
    markNotificationAsRead: async function ({ commit, dispatch, state }, notificationId) {
      const employee_id = state.user?.data?.employee_id;
      if (!employee_id || !notificationId) return;

      // Optimistic: the dot and badge clear instantly; the refetch below
      // reconciles with the server either way (and reverts on failure).
      commit("MARK_NOTIFICATIONS_READ", [notificationId]);
      try {
        const apis = (await import("../apis/index")).default;
        await apis.updateNotificationDetails({
          notification_id: notificationId,
          employee_id: employee_id,
          is_read: 1,
          changed_by: employee_id
        });
      } catch (error) {
        console.error("Error marking notification as read:", error);
      } finally {
        dispatch("fetchNotifications");
      }
    },
    markAllNotificationsAsRead: async function ({ commit, dispatch, state }) {
      const employee_id = state.user?.data?.employee_id;
      if (!employee_id) return;

      const unreadIds = (state.notifications || [])
        .filter(isUnreadNotification)
        .map(n => n.notification_id)
        .filter(Boolean);
      if (!unreadIds.length) return;

      commit("MARK_NOTIFICATIONS_READ", unreadIds);
      try {
        const apis = (await import("../apis/index")).default;
        // updateNotificationDetails is per-notification, so fan out; one
        // failure must not stop the rest.
        const results = await Promise.allSettled(
          unreadIds.map(id =>
            apis.updateNotificationDetails({
              notification_id: id,
              employee_id: employee_id,
              is_read: 1,
              changed_by: employee_id
            })
          )
        );
        const failed = results.filter(r => r.status === "rejected").length;
        if (failed) console.error(`Failed to mark ${failed} notification(s) as read`);
      } finally {
        dispatch("fetchNotifications");
      }
    },
  },
  modules: {},
});
